import React, { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  SearchUserQuery,
  SearchUserQueryVariables,
} from "../../schemaTypes";

const searchUserQuery = gql`
  query SearchUser(
    $input: SearchUserInput!, 
    $limit: Int!, 
    $offset: Int!
  ) {
    searchUser(
      input: $input, 
      limit: $limit,
      offset: $offset
    ) {
      ... on CustomerPWA {
        __typename
        id
        firstname
        lastname
        email
        phone
        username
      }
      ... on AdminPWA {
        __typename
        id
        merchantProfile {
          id
          companyName
          username
          companyLocation
          logoUrl
          bannerUrl
          description
          websiteOrSocials
        }
      }
    }
  }
`;

interface WithSearchUser {
  results: SearchUserQuery['searchUser'];
  loading: boolean;
  loadMore: () => void;
  hasMoreResults: boolean;
}

interface Props {
  variables: {
    input: { searchText: string };
    limit: number;
    offset: number;
  };
  children: (data: WithSearchUser) => React.ReactElement | null;
}


export const SearchUser: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    SearchUserQuery, 
    SearchUserQueryVariables
  >(
    searchUserQuery,{
      variables,
      notifyOnNetworkStatusChange: true,
    }
  );

  const results = data?.searchUser ?? [];
  const hasMoreResults = results.length % variables.limit === 0 && results.length > 0;

  const loadMore = useCallback(() => {
    fetchMore({
      variables: {
        ...variables,
        offset: results.length,
      },
      updateQuery: (
        prev: SearchUserQuery,
        { fetchMoreResult }: { fetchMoreResult?: SearchUserQuery }
      ) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          searchUser: [
            ...(prev.searchUser || []),
            ...(fetchMoreResult.searchUser || []),
          ],
        };
      },
    });
  }, [fetchMore, variables, results.length]);

  return children({
    hasMoreResults,
    results,
    loading,
    loadMore,
  });
};